import React, { useRef, useEffect, useState } from 'react';
import { ExternalLink } from 'lucide-react';

function ProjectCard({ project, index, onSelect, isMobile }) {
  const cardRef = useRef(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [isVisible, setIsVisible] = useState(false);
  const observerRef = useRef(null);

  useEffect(() => {
    const el = observerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleMouseMove = (e) => {
    if (isMobile || !cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    setTilt({ x: x * 12, y: -(y * 12) });
  };

  const handleMouseLeave = () => setTilt({ x: 0, y: 0 });

  return (
    <div
      ref={(el) => {
        cardRef.current = el;
        observerRef.current = el;
      }}
      onClick={() => onSelect(project)}
      className={`group cursor-pointer transition-all duration-700 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
      style={{
        transitionDelay: `${index * 0.1}s`,
        perspective: '1000px',
      }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <div
        className="relative overflow-hidden rounded-2xl glass border border-white/[0.06] transition-all duration-700 h-full"
        style={{
          transform: `perspective(1000px) rotateX(${tilt.y}deg) rotateY(${tilt.x}deg)`,
          transition: 'transform 0.5s cubic-bezier(0.23, 1, 0.32, 1)',
        }}
      >
        {/* Glow border on hover */}
        <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none">
          <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-accent-blue/10 via-transparent to-accent-purple/10" />
        </div>

        {/* Image / Video */}
        <div className="relative h-52 lg:h-60 overflow-hidden">
          {project.mediaType === 'video' ? (
            <video
              src={project.image}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              muted
              loop
              autoPlay
              playsInline
              onContextMenu={(e) => e.preventDefault()}
              controlsList="nodownload"
              disablePictureInPicture
            />
          ) : (
            <img
              src={project.image}
              alt={project.title}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              loading="lazy"
            />
          )}

          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-dark-950 via-dark-950/60 to-transparent" />

          {/* Top badge */}
          <div className="absolute top-3 left-3">
            <span className={`px-2.5 py-1 rounded-lg text-[10px] font-mono font-medium uppercase tracking-wider ${
              project.title.includes('Finalizado')
                ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                : 'bg-accent-blue/10 text-accent-cyan border border-accent-blue/20'
            }`}>
              {project.title.includes('Finalizado') ? 'Finalizado' : 'En Progreso'}
            </span>
          </div>

          {/* Info at bottom of image */}
          <div className="absolute bottom-0 left-0 right-0 p-4">
            <h3 className="font-display font-bold text-base lg:text-lg text-white mb-1 line-clamp-1">
              {project.title}
            </h3>
            <p className="text-xs text-gray-400 line-clamp-1">
              {project.platform}
            </p>
          </div>
        </div>

        {/* Card footer */}
        <div className="p-4 space-y-3">
          <p className="text-xs text-gray-500 leading-relaxed line-clamp-2 group-hover:text-gray-400 transition-colors duration-300">
            {project.description}
          </p>

          <div className="flex items-center justify-between">
            <div className="flex flex-wrap gap-1.5">
              {project.technologies.slice(0, isMobile ? 2 : 3).map((tech, i) => (
                <span
                  key={i}
                  className="px-2 py-0.5 text-[10px] font-mono rounded-md bg-white/[0.04] border border-white/[0.06] text-gray-500"
                >
                  {tech}
                </span>
              ))}
              {project.technologies.length > (isMobile ? 2 : 3) && (
                <span className="px-2 py-0.5 text-[10px] rounded-md bg-accent-blue/10 text-accent-blue border border-accent-blue/20">
                  +{project.technologies.length - (isMobile ? 2 : 3)}
                </span>
              )}
            </div>

            <ExternalLink className="w-3.5 h-3.5 text-gray-600 group-hover:text-accent-blue transition-colors duration-300 flex-shrink-0" />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Projects({ projects, onSelect, visible, isMobile }) {
  const titleRef = useRef(null);
  const [titleVisible, setTitleVisible] = useState(false);

  useEffect(() => {
    if (visible) {
      setTimeout(() => setTitleVisible(true), 100);
    } else {
      setTitleVisible(false);
    }
  }, [visible]);

  return (
    <section className="relative min-h-screen px-6 lg:px-12 py-24 lg:py-32">
      {/* Section header */}
      <div
        ref={titleRef}
        className={`max-w-7xl mx-auto mb-12 lg:mb-16 transition-all duration-700 ${
          titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="inline-flex items-center gap-3 mb-4">
          <div className="w-8 h-[2px] bg-gradient-to-r from-accent-blue to-accent-purple rounded-full" />
          <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-gray-500">
            Portfolio
          </span>
          <div className="w-8 h-[2px] bg-gradient-to-r from-accent-purple to-accent-blue rounded-full" />
        </div>
        <h2 className="font-display font-bold text-3xl lg:text-5xl text-white mb-4">
          Proyectos{' '}
          <span className="text-gradient">Destacados</span>
        </h2>
        <p className="text-sm lg:text-base text-gray-500 max-w-2xl">
          Una muestra representativa de mi trabajo, centrada en la creación,
          programación y diseño de experiencias interactivas.
        </p>
      </div>

      {/* Projects Grid */}
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 lg:gap-6">
          {projects.map((project, index) => (
            <ProjectCard
              key={project.id}
              project={project}
              index={index}
              onSelect={onSelect}
              isMobile={isMobile}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
